/**
 * Compliance layer — shared types.
 *
 * Ported and extended from x365's frameworks/mappings.js. This is the "test once,
 * satisfy many" foundation (MERGE-PLAN §4): one audit control test maps to controls
 * across six frameworks, so a single finding produces evidence for all of them.
 *
 * Pure data + types only — no Graph calls, no React. Everything here is
 * deterministic and unit-testable.
 */

/** Audit severity taxonomy (x365 four-level model, drives the posture weighting). */
export type AuditSeverity = 'critical' | 'high' | 'medium' | 'low';

/** Which ported audit module produces a control's finding. */
export type AuditModule = 'intune' | 'identity' | 'applications';

/** The frameworks we map every control to. */
export type FrameworkId = 'cis' | 'nist' | 'iso' | 'soc2' | 'hipaa' | 'secScore';

/** Stable, machine-readable identifier for a control test (the x365 "finding key"). */
export type ControlId = string;

/** One control in the unified library. */
export interface ControlDef {
  /** Stable key, e.g. 'identity_user_no_mfa'. */
  id: ControlId;
  /** Human-readable finding title (what fails when the control is not met). */
  title: string;
  /** What "good" looks like — the control objective an auditor reads. */
  objective: string;
  module: AuditModule;
  severity: AuditSeverity;
  /** One concise remediation step. */
  remediation: string;
  /** Framework citations. `secScore` is informational (Microsoft action labels). */
  frameworks: Record<FrameworkId, readonly string[]>;
}

export const FRAMEWORK_LABELS: Record<FrameworkId, string> = {
  cis: 'CIS M365 Benchmark',
  nist: 'NIST SP 800-53 Rev 5',
  iso: 'ISO/IEC 27001:2022',
  soc2: 'SOC 2 (TSC)',
  hipaa: 'HIPAA Security Rule',
  secScore: 'Microsoft Secure Score',
};

/** Frameworks that are certifiable/attestable (excludes the informational Secure Score). */
export const CERTIFIABLE_FRAMEWORKS: readonly FrameworkId[] = [
  'cis',
  'nist',
  'iso',
  'soc2',
  'hipaa',
];

/** Higher weight = worse; drives the severity-weighted posture score. */
export const SEVERITY_WEIGHT: Record<AuditSeverity, number> = {
  critical: 25,
  high: 15,
  medium: 8,
  low: 3,
};

export const SEVERITY_ORDER: readonly AuditSeverity[] = [
  'critical',
  'high',
  'medium',
  'low',
];
